import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { SITE } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import { LangSwitcher } from "@/components/LangSwitcher";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { t, to } = useLang();

  return (
    <header
      className="sticky top-0 z-50 border-b border-line bg-cream/85 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="container-x flex h-16 items-center justify-between sm:h-20">
        <Link to={to("/")} className="flex items-center gap-3" data-testid="header-logo">
          <span className="block h-2.5 w-2.5 rounded-sm bg-coral" />
          <span className="text-sm font-bold tracking-tight text-navy sm:text-base">{SITE.name}</span>
        </Link>

        <nav className="hidden items-center gap-9 md:flex" data-testid="desktop-nav">
          {t.nav.map((item) => (
            <NavLink
              key={item.path}
              to={to(item.path)}
              end={item.path === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `relative text-sm font-medium transition-colors duration-300 ${
                  isActive ? "text-brand" : "text-muted hover:text-navy"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-dot"
                      className="absolute -bottom-2 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-brand"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
          <Link to={to("/apps")} className="btn-primary !px-5 !py-2.5" data-testid="nav-cta-apps">
            {t.header.cta} <ArrowUpRight size={15} />
          </Link>
          <span className="h-5 w-px bg-line" />
          <LangSwitcher />
        </nav>

        <button
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-navy md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-line bg-cream md:hidden"
            data-testid="mobile-nav"
          >
            <div className="container-x flex flex-col py-4">
              {t.nav.map((item, i) => (
                <NavLink
                  key={item.path}
                  to={to(item.path)}
                  end={item.path === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${item.testId}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between border-b border-line py-4 text-lg font-semibold ${
                      isActive ? "text-brand" : "text-navy"
                    }`
                  }
                >
                  <span>{item.label}</span>
                  <span className="font-mono text-xs text-coral">0{i + 1}</span>
                </NavLink>
              ))}
              <div className="flex items-center justify-between py-5">
                <span className="eyebrow">{t.header.language}</span>
                <div onClick={() => setOpen(false)}>
                  <LangSwitcher mobile />
                </div>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
